import React from 'react';
import { OutlinedTextInput } from 'library';
import { Autocomplete } from '@mui/material';
import type { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

type False = false;

interface IAutoCompleteProps<T, M extends boolean = False>
    extends Omit<
        AutocompleteProps<T, M, False, False>,
        | 'value'
        | 'onChange'
        | 'options'
        | 'multiple'
        | 'renderInput'
        | 'renderOption'
        | 'getOptionLabel'
        | 'isOptionEqualToValue'
        | 'getOptionDisabled'
        | 'onSelect'
    > {
    multiple: M;
    options: T[];
    selected: M extends true ? T[] : T | null;
    onSelect: (value: M extends true ? T[] : T | null) => void;

    getOptionLabel: (option: T) => string;
    getOptionSelected?: (option: T, value: T) => boolean;
    getOptionDisabled?: (option: T) => boolean;

    renderOption?: (option: T) => React.ReactNode;
}

export default function AutoComplete<T, M extends boolean = False>({
    multiple,
    options,
    selected,
    onSelect,
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    renderOption,
    ...rest
}: IAutoCompleteProps<T, M>) {
    const customRenderInput = (params: AutocompleteRenderInputParams) => (
        <OutlinedTextInput
            {...params}
            style={{ marginTop: 10 }}
            variant="outlined"
            placeholder="Click here to search"
        />
    );

    return (
        <Autocomplete<T, M, False, False>
            multiple={multiple}
            value={selected}
            options={options}
            onChange={(_, value) => onSelect(value)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={getOptionSelected}
            getOptionDisabled={getOptionDisabled}
            renderOption={(props, option) => {
                const content = renderOption ? renderOption(option) : getOptionLabel(option);
                return <li {...props}>{content}</li>;
            }}
            renderInput={customRenderInput}
            {...rest}
        />
    );
}
